/* eslint-disable react-hooks/exhaustive-deps */
import { HSCard, HSCardSkeleton, HSSearchInput } from '@/Components/Common/HSWidget';
import FilterBar, { sanitizeFilters } from '@/Components/Common/FilterBar';
import { SESSION_STORAGE_KEYS } from '@/constants/device';
import { LoadingOutlined } from '@ant-design/icons';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import useStartVideoCall from '@/Components/Hooks/useStartVideoCall';
import {
  isStaffActionAllowed,
  KnownActionsList,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import { getUsername } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { Actions, PhotoResolution } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { Divider, Empty, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import MainPage from '../../MainPage';
import {
  AddNewScheduleIcon,
  AddPlusIcon,
  CallIcon,
  ContactImage,
  EditNewIcon,
  ResendEmailIcon,
  ShieldIconCheck,
} from '../Common/Icons';

const STAFF_ROLE_FILTER_GROUP = 'role';
const STAFF_ROLE_FILTER_LABELS = {
  staffAndStaffAdmin: 'STAFFANDSTAFFADMIN',
  staffAdmin: 'STAFFADMIN',
  staff: 'STAFF',
};
const STAFF_ROLE_FILTER_ROLES = {
  staffAndStaffAdmin: ['Staff', 'Staff Admin'],
  staffAdmin: ['Staff Admin'],
  staff: ['Staff'],
};

const ListStaff = ({ parentBread, staffList, fetchingStaffList, onActionClick }) => {
  const header = parentBread || [];
  const trans = useSelector(translator);
  const permissions = useSelector(getPermissions());

  const filterGroups = useMemo(
    () => [
      {
        key: STAFF_ROLE_FILTER_GROUP,
        children: Object.keys(STAFF_ROLE_FILTER_LABELS).map((key) => ({
          key,
          label: trans(STAFF_ROLE_FILTER_LABELS[key]),
        })),
      },
    ],
    [trans]
  );

  const [searchText, setSearchText] = useState('');
  const [savedFilters, setSavedFilters] = useState(
    () => SessionStorage.get(SESSION_STORAGE_KEYS.STAFF_FILTER) || []
  );
  const appliedFilter = useMemo(
    () => sanitizeFilters(savedFilters, filterGroups),
    [savedFilters, filterGroups]
  );
  const isFiltered = !!searchText || appliedFilter.length > 0;

  const { startCall } = useStartVideoCall();
  const currentUser = useSelector(getCurrentUser);
  const userApi = useSelector((state) => state.apis.userApi);

  const allowCallStaff = useMemo(
    () =>
      isStaffActionAllowed(currentUser?.permission_level, KnownActionsList.call_staff, permissions),
    [currentUser.permission_level]
  );
  const allowCreateStaffEvent = useMemo(
    () =>
      isStaffActionAllowed(
        currentUser?.permission_level,
        KnownActionsList.create_staff_events,
        permissions
      ),
    [currentUser.permission_level]
  );
  const allowManageAccounts = useMemo(
    () =>
      isStaffActionAllowed(
        currentUser?.permission_level,
        KnownActionsList.manage_accounts,
        permissions
      ),
    [currentUser.permission_level]
  );

  const filteredList = useMemo(() => {
    const query = searchText.toLowerCase();
    const roleFilter = appliedFilter[0]?.itemKey;
    return staffList.filter(
      (item) =>
        (!roleFilter || STAFF_ROLE_FILTER_ROLES[roleFilter].includes(item.permission_level)) &&
        (!query || getUsername(item)?.toLowerCase().includes(query))
    );
  }, [staffList, searchText, appliedFilter]);

  const handleSearchClear = () => {
    setSearchText('');
  };

  const handleFilterChange = (filters) => {
    setSavedFilters(filters);
    SessionStorage.set(SESSION_STORAGE_KEYS.STAFF_FILTER, filters);
  };

  const renderHeaderActions = (
    <div className="header-action-container">
      <div className="search-container">
        <HSSearchInput
          autoFocus
          value={searchText}
          onChange={setSearchText}
          onClear={handleSearchClear}
        />
      </div>
      <FilterBar groups={filterGroups} selected={appliedFilter} onChange={handleFilterChange} />
    </div>
  );

  const renderItemCounter = (
    <div className="item-count-container" style={{ padding: '10px', cursor: 'default' }}>
      <span className="item-count">
        {isFiltered ? `${filteredList?.length ?? 0} ${trans('FOUND_L')} | ` : ''}
        {trans('TOTAL')}
        {' ('}
        {fetchingStaffList ? <LoadingOutlined /> : (staffList.length ?? 0)} {')'}
      </span>
    </div>
  );

  const staff_role_translated = (role) => {
    switch (role) {
      case 'Staff':
        return trans('STAFF');
      case 'Staff Admin':
        return (
          <>
            <ShieldIconCheck style={{ fill: '#1C1C1C' }} />
            <span>{trans('STAFFADMIN')}</span>
          </>
        );
      default:
        return role;
    }
  };

  const onResendEmailHandler = (staffuseruuid) => {
    const errorCallback = (error) => {
      const msg = `Error Sending Email details`;
      console.error(msg, error);
      // message.error(msg);
    };
    const successCallback = (data) => {
      const msg = `Email Sent`;
      // message.success(msg);
      onActionClick(null, null, true)();
    };
    try {
      const user = {
        useruuid: staffuseruuid,
        updateprofile: true,
      };
      userApi.updateUser(
        user,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const renderStaff = (staff) => {
    const staffrole = staff.permission_level ? staff.permission_level : staff.rolename;
    const translatedRole = staff_role_translated(staffrole);
    return (
      <HSCard
        key={staff.useruuid}
        className="item-container staff-container container-card-css"
        content={
          <>
            <div className="container-1">
              <div className="user-info-container">
                <div className="image-container">
                  {staff.profilePics[PhotoResolution.R64] ? (
                    <img
                      src={staff.profilePics[PhotoResolution.R64]}
                      className="user-profile-pic border-profile-pic"
                      alt=""
                    />
                  ) : (
                    <ContactImage className="user-profile-pic" />
                  )}
                </div>
                <div className="info-container">
                  <div className="username">{getUsername(staff)}</div>
                </div>
              </div>
              <div
                className="role"
                style={{ display: 'flex', alignItems: 'left', cursor: 'default' }}
              >
                <div
                  className={translatedRole === trans('STAFF') ? 'staffBadge' : 'staffAdminBadge'}
                >
                  {translatedRole}
                </div>
              </div>
              <Divider type="vertical" className="divider" />
              <div>
                <div className="pending-action-container">
                  {staff?.emailVerified === false ? (
                    <Tooltip title={trans('PENDINGINVITE')}>
                      <div className="action">
                        <div className="pendingBadge">{trans('PENDING')}</div>
                      </div>
                    </Tooltip>
                  ) : (
                    <div className="empty-action" />
                  )}
                </div>
              </div>
            </div>
            <div className="container-2">
              {/* <span className="provider">
            {formatString(staff.staffrole, StringFormats.pascal)}
          </span> */}

              {/* <div className="relation">{staff.relationship}</div> */}
              {/* <div className="list-association-count-container">
            <AccountIcon className="count-icon" style={{color:"black"}}/>
            <div className="count-container">
              <span className="count-text">{staff.numofaccounts ?? 0}</span>
            </div>
          </div> */}
              {currentUser?.permission_level === 'Staff Admin' && staff?.emailVerified === false ? (
                <Tooltip title={trans('RESENDEMAIL')}>
                  <div className="action">
                    <ResendEmailIcon onClick={() => onResendEmailHandler(staff.useruuid)} />
                  </div>
                </Tooltip>
              ) : (
                <div className="empty-action" />
              )}
              <Divider type="vertical" />
              <div className="actions-container staff-action-containers">
                {currentUser.useruuid !== staff.useruuid &&
                allowCallStaff &&
                staff?.emailVerified === true ? (
                  <Tooltip title={trans('VIDEOCALL')}>
                    <div
                      className="action"
                      onClick={() => startCall({ callees: [staff], isStaff: true })}
                    >
                      <CallIcon />
                    </div>
                  </Tooltip>
                ) : (
                  <div className="empty-action" />
                )}
                {allowCreateStaffEvent && staff?.emailVerified === true ? (
                  <Tooltip title={trans('SCHEDULEEVENT')}>
                    <div className="action">
                      <AddNewScheduleIcon onClick={onActionClick(Actions.addEvent, staff)} />
                    </div>
                  </Tooltip>
                ) : (
                  <div className="empty-action" />
                )}
                {allowManageAccounts ? (
                  <Tooltip title={trans('EDIT')}>
                    <div className="action">
                      <EditNewIcon
                        className="edit-icon"
                        onClick={onActionClick(Actions.editStaff, staff)}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <div className="empty-action" />
                )}
              </div>
            </div>
          </>
        }
      />
    );
  };

  return (
    <MainPage hasSider={true} header={header}>
      <div className="liststaff-wrapper page-content-wrapper">
        <div className="page-header-container page-header-container-css">
          <span className="page-header">{trans('STAFF')}</span>
          {renderHeaderActions}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '50px 50px 35px',
          }}
        >
          {isStaffActionAllowed(
            currentUser?.permission_level,
            KnownActionsList.manage_accounts,
            permissions
          ) && (
            <AddPlusIcon
              style={{ width: '60px', height: '60px', cursor: 'pointer' }}
              onClick={onActionClick(Actions.addStaff, null)}
            />
          )}
          {renderItemCounter}
        </div>

        <div className="item-list-container staff-list-container">
          {fetchingStaffList ? (
            <HSCardSkeleton />
          ) : !filteredList.length ? (
            <Empty />
          ) : (
            filteredList.map(renderStaff)
          )}
        </div>
      </div>
    </MainPage>
  );
};

ListStaff.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  staffList: PropTypes.arrayOf(PropTypes.object),
  fetchingStaffList: PropTypes.bool,
  onActionClick: PropTypes.func,
};

export default ListStaff;
